import { Head, router } from "@inertiajs/react"
import React, { useState } from "react"
import { track } from "@/lib/analytics"
import type { SocialConnection } from "@/types"
import DashboardLayout from "@/components/dashboard/layout"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

type Platform = "instagram" | "facebook" | "tiktok" | "youtube"

const PLATFORMS: {
  key: Platform
  name: string
  colour: string
  icon: (props: { className?: string }) => React.JSX.Element
}[] = [
  { key: "instagram", name: "Instagram", colour: "#E1306C", icon: InstagramIcon },
  { key: "facebook", name: "Facebook", colour: "#1877F2", icon: FacebookIcon },
  { key: "tiktok", name: "TikTok", colour: "#000000", icon: TiktokIcon },
  { key: "youtube", name: "YouTube", colour: "#FF0000", icon: YoutubeIcon },
]

export default function ConnectionsIndex({
  connections,
  can_manage,
}: {
  connections: SocialConnection[]
  can_manage: boolean
}) {
  return (
    <DashboardLayout>
      <Head title="Connections | QuiteRight" />

      <div className="mb-8">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-charcoal">
          Connections
        </h1>
        <p className="mt-1 text-sm text-warm-grey">
          Connect your social accounts to publish videos directly from QuiteRight.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        {PLATFORMS.map((platform) => {
          const connection = connections.find((c) => c.platform === platform.key)
          return (
            <PlatformCard
              key={platform.key}
              platform={platform}
              connection={connection}
              canManage={can_manage}
            />
          )
        })}
      </div>

    </DashboardLayout>
  )
}

function PlatformCard({
  platform,
  connection,
  canManage,
}: {
  platform: (typeof PLATFORMS)[number]
  connection?: SocialConnection
  canManage: boolean
}) {
  const [disconnecting, setDisconnecting] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const isConnected = !!connection && connection.status === "active"
  const isLocked = !canManage && !isConnected
  const Icon = platform.icon

  function handleConnect() {
    track("social_connection_initiated", { platform: platform.key })
    // OmniAuth 2.x requires POST to initiate OAuth (CSRF protection).
    // Submit a hidden form with the Rails CSRF token.
    const form = document.createElement("form")
    form.method = "POST"
    form.action = `/auth/social/social_${platform.key}`

    const tokenInput = document.createElement("input")
    tokenInput.type = "hidden"
    tokenInput.name = "authenticity_token"
    const meta = document.querySelector('meta[name="csrf-token"]')
    tokenInput.value = meta?.getAttribute("content") || ""
    form.appendChild(tokenInput)

    document.body.appendChild(form)
    form.submit()
  }

  function handleDisconnect() {
    if (!connection) return
    track("social_connection_disconnected", { platform: platform.key })
    setDisconnecting(true)
    router.delete(`/dashboard/connections/${connection.id}`, {
      preserveScroll: true,
      onFinish: () => {
        setDisconnecting(false)
        setShowConfirm(false)
      },
    })
  }

  return (
    <>
      <div
        className={`group relative flex flex-col rounded-xl border bg-white transition-all ${
          isLocked
            ? "border-stone/30 opacity-60"
            : isConnected
              ? "border-stone/60 shadow-sm"
              : "border-dashed border-stone/50 hover:border-stone/80 hover:shadow-sm"
        }`}
      >
        {/* Header row */}
        <div className="flex items-center gap-3.5 px-5 pt-5 pb-4">
          <div
            className="flex size-11 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${platform.colour}10` }}
          >
            <Icon className="size-5" />
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-charcoal">{platform.name}</h3>
            {isLocked ? (
              <p className="mt-0.5 text-xs text-warm-grey/60">Coming soon</p>
            ) : isConnected ? (
              <p className="mt-0.5 truncate text-xs text-warm-grey">
                @{connection.platform_username}
              </p>
            ) : (
              <p className="mt-0.5 text-xs text-warm-grey/60">Not connected</p>
            )}
          </div>

          {/* Status badge */}
          {isLocked ? (
            <span className="flex items-center gap-1.5 rounded-full bg-stone/10 px-2.5 py-1">
              <svg className="size-3 text-warm-grey/60" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
              </svg>
              <span className="text-[10px] font-semibold tracking-wide text-warm-grey/60">
                Soon
              </span>
            </span>
          ) : isConnected && (
            <span className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1">
              <span className="size-1.5 rounded-full bg-emerald-500" />
              <span className="text-[10px] font-semibold tracking-wide text-emerald-700">
                Live
              </span>
            </span>
          )}
        </div>

        {/* Connected details */}
        {isConnected && (
          <div className="border-t border-stone/30 px-5 py-3">
            {connection.managed_pages && connection.managed_pages.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-warm-grey/70">
                  {connection.managed_pages.length === 1 ? "Page" : "Pages"}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {connection.managed_pages.map((page) => (
                    <li key={page.id} className="flex items-center gap-1.5 text-xs text-warm-grey">
                      <span>{page.name}</span>
                      {page.category && (
                        <span className="text-warm-grey/50">· {page.category}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {connection.connected_at && (
              <p className={`text-[11px] text-warm-grey/50 ${connection.managed_pages && connection.managed_pages.length > 0 ? "mt-2" : ""}`}>
                Connected {new Date(connection.connected_at).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            )}
          </div>
        )}

        {/* Action footer */}
        <div className={`mt-auto px-5 pb-4 ${isConnected ? "" : "pt-0"}`}>
          {isLocked ? null : isConnected ? (
            canManage && (
              <button
                type="button"
                className="text-xs text-warm-grey/60 transition-colors hover:text-red"
                onClick={() => setShowConfirm(true)}
                disabled={disconnecting}
              >
                Disconnect
              </button>
            )
          ) : (
            canManage && (
              <Button
                variant="secondary"
                size="sm"
                className="h-8 w-full text-xs font-medium"
                onClick={handleConnect}
              >
                Connect {platform.name}
              </Button>
            )
          )}
        </div>
      </div>

      {/* Disconnect confirmation */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {platform.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will revoke QuiteRight's access to post on your {platform.name} account.
              You can reconnect at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              className="bg-red text-white hover:bg-red/90"
            >
              {disconnecting ? "Disconnecting..." : "Disconnect"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// --- Platform Icons ---

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <rect x="2" y="2" width="20" height="20" rx="5" stroke="#E1306C" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="5" stroke="#E1306C" strokeWidth="1.5" />
      <circle cx="17.5" cy="6.5" r="1" fill="#E1306C" />
    </svg>
  )
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#1877F2">
      <path d="M24 12c0-6.627-5.373-12-12-12S0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854V15.47H7.078V12h3.047V9.356c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.875V12h3.328l-.532 3.47h-2.796v8.385C19.612 22.954 24 17.99 24 12Z" />
    </svg>
  )
}

function TiktokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1 0-5.78c.29 0 .57.04.84.12V9.01a6.32 6.32 0 0 0-.84-.06 6.34 6.34 0 0 0 0 12.68 6.34 6.34 0 0 0 6.34-6.34V9.37a8.16 8.16 0 0 0 4.77 1.52V7.46a4.85 4.85 0 0 1-1.01-.77Z" />
    </svg>
  )
}

function YoutubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#FF0000">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814ZM9.545 15.568V8.432L15.818 12l-6.273 3.568Z" />
    </svg>
  )
}
